/**
 * Wishlist page (sections/main-wishlist.liquid)
 * <wishlist-page>
 *  - Reads window.themeWishlist (browser storage), merges the signed-in customer's saved list
 *    (customer metafield, plus optional sync URL), and keeps the sync URL updated on every change
 *  - Renders each item with the real product card: /products/{handle}?view=wishlist-item&variant={id}
 *    (live availability + price in the visitor's currency); removes items whose product is gone
 *  - Summary: in stock / product count / total value (in-stock items), "Process to checkout"
 *    adds all in-stock items to the cart and opens checkout
 *
 * Sync URL contract (app proxy or any backend on the shop domain):
 *   GET  {sync}            -> { "items": [{ "productId": "1", "variantId": "2", "handle": "tee" }] }
 *   POST {sync} { items }  -> 2xx
 */
(() => {
  if (customElements.get('wishlist-page')) return;

  const CONCURRENCY = 6;
  const SYNC_DELAY = 800;

  class WishlistPage extends HTMLElement {
    #controller = new AbortController();
    /** @type {HTMLElement | null} */
    #list = null;
    #syncTimer = 0;
    /** Product ids already requested or rendered (prevents duplicate cards). */
    #known = new Set();

    async connectedCallback() {
      this.#list = this.querySelector('[data-wishlist-list]');
      if (!window.themeWishlist) {
        await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
      }
      if (!window.themeWishlist) return;

      const { signal } = this.#controller;
      this.#mergeAccountItems();
      await this.#pullSync();

      document.addEventListener('wishlist:change', this.#onChange.bind(this), { signal });
      this.querySelector('[data-wishlist-checkout]')?.addEventListener('click', this.#checkout.bind(this), { signal });

      await this.#renderAll();
    }

    disconnectedCallback() {
      this.#controller.abort();
      this.#controller = new AbortController();
      clearTimeout(this.#syncTimer);
    }

    /* ---------- Account sync ---------- */

    #mergeAccountItems() {
      try {
        const items = JSON.parse(this.querySelector('[data-account-items]')?.textContent || '[]');
        if (Array.isArray(items) && items.length) window.themeWishlist.merge(items, 'account');
      } catch {
        // Invalid metafield JSON - ignore, browser list still works.
      }
    }

    async #pullSync() {
      const url = this.dataset.syncUrl;
      if (!url) return;
      try {
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const data = await response.json();
        if (Array.isArray(data?.items)) window.themeWishlist.merge(data.items, 'account');
      } catch (error) {
        console.warn('[wishlist] sync pull failed', error);
      }
    }

    #pushSync() {
      const url = this.dataset.syncUrl;
      if (!url) return;
      clearTimeout(this.#syncTimer);
      this.#syncTimer = setTimeout(() => {
        fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: this.dataset.customerId, items: window.themeWishlist.get() })
        }).catch((error) => console.warn('[wishlist] sync push failed', error));
      }, SYNC_DELAY);
    }

    /* ---------- Rendering ---------- */

    async #renderAll() {
      const items = [...window.themeWishlist.get()].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
      this.#list.replaceChildren();
      this.#known.clear();

      const queue = [...items];
      const worker = async () => {
        while (queue.length) {
          const item = queue.shift();
          await this.#renderItem(item);
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));

      // Keep the saved order (newest first) regardless of which request finished first.
      for (const item of items) {
        const node = this.#list.querySelector(`[data-product-id="${item.productId}"]`);
        if (node) this.#list.appendChild(node);
      }
      this.#updateState();
    }

    /** @param {{productId: string, variantId: string, handle?: string}} item */
    async #renderItem(item) {
      const id = String(item.productId);
      if (!item.handle || this.#known.has(id)) return;
      this.#known.add(id);
      const url = new URL(`${this.dataset.rootUrl || '/'}products/${item.handle}`.replace('//products', '/products'), window.location.origin);
      url.searchParams.set('view', 'wishlist-item');
      if (item.variantId) url.searchParams.set('variant', item.variantId);

      try {
        const response = await fetch(url, { signal: this.#controller.signal });
        if (response.status === 404) {
          this.#known.delete(id);
          window.themeWishlist.remove(item.productId); // product deleted or unpublished
          return;
        }
        if (!response.ok) {
          this.#known.delete(id);
          return;
        }
        const template = document.createElement('template');
        template.innerHTML = (await response.text()).trim();
        const node = template.content.querySelector('[data-wishlist-item]');
        if (node) this.#list.appendChild(node);
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('[wishlist] could not load', item.handle, error);
      }
    }

    /** @param {CustomEvent} event */
    #onChange(event) {
      const items = event.detail?.items || [];
      const ids = new Set(items.map((item) => String(item.productId)));

      for (const node of this.#list.querySelectorAll('[data-wishlist-item]')) {
        if (ids.has(node.dataset.productId)) continue;
        this.#known.delete(node.dataset.productId);
        node.classList.add('is-removing');
        setTimeout(() => {
          node.remove();
          this.#updateState();
        }, 250);
      }

      const added = items.filter((item) => !this.#known.has(String(item.productId)));
      if (added.length) Promise.all(added.map((item) => this.#renderItem(item))).then(() => this.#updateState());

      if (event.detail?.source !== 'account') this.#pushSync();
      this.#updateState();
    }

    /* ---------- Summary ---------- */

    #updateState() {
      const nodes = [...this.#list.querySelectorAll('[data-wishlist-item]:not(.is-removing)')];
      const inStock = nodes.filter((node) => node.dataset.available === 'true');
      const total = inStock.reduce((sum, node) => sum + (Number(node.dataset.price) || 0), 0);
      const empty = nodes.length === 0;

      this.dataset.state = 'ready';
      this.querySelector('[data-wishlist-empty]').hidden = !empty;
      this.#list.hidden = empty;

      const summary = this.querySelector('[data-wishlist-summary]');
      if (!summary) return;
      summary.hidden = false;
      // Function replacement: currency strings like "$164.00" must not be read as "$1" back-references.
      const label = (key, value) => (this.dataset[key] || '').replace(/\[(count|amount)\]/, () => value);
      summary.querySelector('[data-stat-in-stock]').textContent = label('labelInStock', String(inStock.length));
      summary.querySelector('[data-stat-count]').textContent = label('labelCount', String(nodes.length));
      summary.querySelector('[data-stat-total]').textContent = label('labelTotal', this.#formatMoney(total));
      summary.querySelector('[data-wishlist-checkout]').disabled = inStock.length === 0;
    }

    /** @param {number} cents */
    #formatMoney(cents) {
      const currency = this.dataset.currency || 'USD';
      // "pt-PT" / "zh-CN" already carry a region: never append the country again ("pt-PT-IN" is invalid)
      const language = this.dataset.locale || '';
      const candidates = [
        language.includes('-') ? language : [language, this.dataset.country].filter(Boolean).join('-'),
        language.split('-')[0],
        undefined
      ];
      for (const locale of candidates) {
        try {
          return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency }).format(cents / 100);
        } catch {
          // try the next, simpler locale
        }
      }
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }

    /* ---------- Checkout ---------- */

    async #checkout(event) {
      const button = event.currentTarget;
      const status = this.querySelector('[data-wishlist-status]');
      const ids = [...this.#list.querySelectorAll('[data-wishlist-item][data-available="true"]')]
        .map((node) => Number(node.dataset.variantId))
        .filter(Boolean);
      if (!ids.length) return;

      const original = button.textContent;
      button.classList.add('is-loading');
      button.textContent = this.dataset.labelCheckingOut || original;

      const add = (items) => fetch(this.dataset.cartAddUrl ? `${this.dataset.cartAddUrl}.js` : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });

      try {
        let response = await add(ids.map((id) => ({ id, quantity: 1 })));
        if (!response.ok) {
          // One item failed (e.g. sold out since loading): add the rest one by one.
          let added = 0;
          for (const id of ids) {
            response = await add([{ id, quantity: 1 }]);
            if (response.ok) added += 1;
          }
          if (!added) throw new Error('Nothing could be added');
        }
        window.location.href = this.dataset.checkoutUrl || '/checkout';
      } catch (error) {
        console.warn('[wishlist] checkout failed', error);
        if (status) status.textContent = this.dataset.labelError || '';
        button.classList.remove('is-loading');
        button.textContent = original;
      }
    }
  }

  customElements.define('wishlist-page', WishlistPage);
})();
